import I18n from 'I18n';
import getIn from 'immutable-less/transmute/getIn';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import * as InternalPropertyDefaults from 'automation-ui-action-features/object-type-properties/InternalPropertyDefaults';
import * as WebhookRequestTypes from 'automation-ui-action-features/actions/webhook/constants/RequestTypes';
import { emailUrl, formUrl, inboundDbObjectUrlByObjectTypeId, inboundDbObjectPropertyUrl, listUrl, recycleBinUrl, taskBoardUrl, notificationsSettingsUrl } from 'automation-ui-references/routing/hubspotUrls';
import { classicWorkflowUrl, flowEditActionUrl, flowDetailsUrl, flowChangesUrl, flowUrl } from 'automation-ui-routing/workflowsUrls';
import ChatflowName from 'automation-ui-references/customer-data/ChatflowName';
import IntegrationName from 'automation-ui-references/customer-data/IntegrationName';
import OwnerName from 'automation-ui-references/customer-data/OwnerName';
import PropertyName from 'automation-ui-references/object-type-properties/PropertyName';
import WorkflowName from 'automation-ui-references/customer-data/WorkflowName';
import UserName from 'automation-ui-references/customer-data/UserName';
import ContactListName from 'automation-ui-references/customer-data/ContactListName';
import ObjectTypeName from 'automation-ui-references/object-types/ObjectTypeName';
import { CLASSIC, PLATFORM } from 'automation-ui-flow-core/flow/FlowSourceTypes';
import * as ActionTypes from 'automation-ui-action-features/constants/ActionTypes';
import { LogEventTypes, EnrollmentStates, ExecutionStates, EnrollTypes, UnenrollTypes, ClassicUnenrollTypes, TriggerObjectTypes, FilterEventTypes } from 'automation-ui-log-events/constants/LogEventConstants';
import { MissingPropertyErrorTypes } from 'automation-ui-log-events/constants/LogEventMessageConstants';
import * as PropertyValueTypes from 'automation-ui-flow-core/fields/PropertyValueTypes';
import { getAction, getEnrollmentState, getEnrollType, getTriggerEnrollType, getFilterEventType, getUnenrollType, getExecutionState, getErrorType, getObjectId, getActionConnection, getFlowId, getPropertiesFromMessage } from 'automation-ui-log-events/records/LogEvent/utils';
import { getAcceptedBranchOperator, getAcceptedBranchValue, getAcceptedListId, getAssignedOwnerId, getChatflowId, getClassicLogEvent, getClassicTrigger, getClassicTriggerId, getCreatedObjectId, getDeferredTimestamp, getEnrollmentAppId, getExecutionCounts, getInvalidProperties, getListId, getMissingProperties, getPropertyValue, getRecipientEmails, getSalesforceOwnerName, getSalesforceUrl, getSourceFlowId, getUserId, getWebhookResponseBody, getWebhookStatusCode, getWorkflowId } from 'automation-ui-log-events/records/LogEvent/contextMapUtils';
import BasePaths from 'automation-ui-routing/BasePaths';
import Action from 'automation-ui-action-features/records/Action';
import { CONTACT_ID } from 'automation-ui-references/object-types/CRMObjectTypes';
import { SupportLinkKeys } from 'automation-ui-usage-tracking/SupportLinks';
import { isDefaultBranchIndex } from 'automation-ui-action-features/records/recordsUtils';
import TargetPropertyName from 'automation-ui-action-features/actions/set-property/TargetPropertyName';
import { getOperatorOption } from 'automation-ui-action-features/actions/static-branch/StaticBranchOperatorSelect';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const getAcceptedListBranchPosition = logEvent => {
  const listId = getAcceptedListId(logEvent);
  const connection = getActionConnection(logEvent);
  const listBranches = connection.listBranches;
  return listBranches.findIndex(listBranch => listBranch.listId === listId);
};
const getAcceptedListBranchName = logEvent => {
  const matchingListBranchIndex = getAcceptedListBranchPosition(logEvent);
  const connection = getActionConnection(logEvent);
  const listBranches = connection.listBranches;
  if (isDefaultBranchIndex(matchingListBranchIndex)) {
    return connection.defaultBranchName;
  }
  const branchName = getIn([matchingListBranchIndex, 'branchName'], listBranches);
  return branchName;
};
const getAcceptedStaticBranchName = logEvent => {
  const maybeAcceptedBranchName = getAcceptedBranchValue(logEvent);
  if (maybeAcceptedBranchName) {
    const operator = getAcceptedBranchOperator(logEvent);
    const operatorLabel = getOperatorOption(operator).text;
    return `${operatorLabel}: ${maybeAcceptedBranchName}`;
  }
  return null;
};
export const getListBranchOptions = (logEvent, objectTypeId) => {
  return {
    branchName: getAcceptedListBranchName(logEvent),
    branchPosition: getAcceptedListBranchPosition(logEvent) + 1,
    recycleBinUrl: recycleBinUrl(objectTypeId),
    objectType: /*#__PURE__*/_jsx(ObjectTypeName, {
      objectTypeId: objectTypeId
    })
  };
};
const getStaticBranchOptions = logEvent => {
  return {
    branchName: getAcceptedStaticBranchName(logEvent)
  };
};
const getEmailOptions = (actionMetadata, logEvent, objectTypeId) => {
  const emailLink = actionMetadata.contentId ? emailUrl(actionMetadata.contentId) : '';
  const objectId = getObjectId(logEvent);
  const contactLink = inboundDbObjectUrlByObjectTypeId(objectTypeId, objectId);
  return {
    emailLink,
    objectType: /*#__PURE__*/_jsx(ObjectTypeName, {
      objectTypeId: objectTypeId
    }),
    contactLink,
    emailBlockedByHubspotSupportLink: SupportLinkKeys.EMAIL_BLOCKED_BY_HUBSPOT,
    sendEmailActionSupportLink: SupportLinkKeys.SEND_EMAIL_ACTION,
    getHelpWithHubspotSupportLink: SupportLinkKeys.GET_HELP_WITH_HUBSPOT,
    cancelledAbuseSupportLink: SupportLinkKeys.EMAIL_CANCELLED_ABUSE
  };
};
const getOwnerLabel = (logEvent, objectTypeId) => {
  const properties = getIn(['action', 'metadata', 'properties'], logEvent);
  const ownerPropertyAssignment = properties.find(p => p.targetProperty === InternalPropertyDefaults.HUBSPOT_OWNER);
  if (getIn(['value', 'type'], ownerPropertyAssignment) === PropertyValueTypes.STATIC_VALUE) {
    return /*#__PURE__*/_jsx(OwnerName, {
      id: getIn(['value', 'value'], ownerPropertyAssignment) || '',
      href: undefined
    });
  } else {
    return /*#__PURE__*/_jsx(PropertyName, {
      objectTypeId: objectTypeId,
      propertyName: getIn(['value', 'propertyName'], ownerPropertyAssignment) || ''
    });
  }
};
const getTaskOptions = (logEvent, objectTypeId) => {
  const objectId = getCreatedObjectId(logEvent);
  const ownerId = getAssignedOwnerId(logEvent);
  const owner = ownerId ? /*#__PURE__*/_jsx(OwnerName, {
    id: ownerId
  }) : null;
  const recycleBinLink = recycleBinUrl(objectTypeId);
  return {
    taskLink: taskBoardUrl(objectId),
    ownerName: ownerId ? /*#__PURE__*/_jsx(FormattedReactMessage, {
      message: "automation-ui-log-events.messageOptionUtils.create-task.content",
      options: {
        owner
      }
    }) : /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-log-events.messageOptionUtils.create-task.noOne"
    }),
    objectType: /*#__PURE__*/_jsx(ObjectTypeName, {
      objectTypeId: objectTypeId
    }),
    recycleBinLink
  };
};
const getSetPropertyOptions = (actionData, logEvent, objectTypeId) => {
  const action = new Action(actionData);
  const flowId = getIn(['flowId'], action);
  const actionId = getIn(['actionId'], action);
  const actionLink = flowEditActionUrl(flowId, actionId);
  const objectId = getObjectId(logEvent);
  const recordLink = inboundDbObjectUrlByObjectTypeId(objectTypeId, objectId);
  const recycleBinLink = recycleBinUrl(objectTypeId);
  return {
    enrolledObjectType: /*#__PURE__*/_jsx(ObjectTypeName, {
      objectTypeId: objectTypeId
    }),
    actionLink,
    recordLink,
    recycleBinLink,
    targetPropertyName: /*#__PURE__*/_jsx(TargetPropertyName, {
      actionMetadata: action.getMetadata(),
      flowObjectTypeId: objectTypeId
    }),
    updateLifecycleStageSupportLink: SupportLinkKeys.UPDATE_LIFECYCLE_STAGE
  };
};
const getUpdateEmailSubscriptionOptions = () => {
  return {
    manageSubscriptionPreferencesSupportLink: SupportLinkKeys.MANAGE_SUBSCRIPTION_PREFERENCES
  };
};
const getDelayOptions = () => {
  return {
    workflowsFaqDelays: SupportLinkKeys.WORKFLOWS_FAQ_DELAYS
  };
};
const getEmailNotificationOptions = (logEvent, actionMetadata) => {
  if (getExecutionState(logEvent) === ExecutionStates.PARTIAL_SUCCESS) {
    const executionCounts = getExecutionCounts(logEvent);
    return Object.assign({}, executionCounts, {
      notificationsSupportLinkKey: SupportLinkKeys.HOW_TO_ENABLE_NOTIFICATIONS,
      // These need to be empty strings or the function returns undefined
      notificationSettingsUrl: notificationsSettingsUrl('', '')
    });
  }
  return {
    subject: actionMetadata.subject,
    personalizeYourContentLink: SupportLinkKeys.PERSONALIZE_YOUR_CONTENT,
    notificationsSupportLinkKey: SupportLinkKeys.HOW_TO_ENABLE_NOTIFICATIONS,
    // These need to be empty strings or the function returns undefined
    notificationSettingsUrl: notificationsSettingsUrl('', '')
  };
};
const getClassicEmailNotificationOptions = (actionMetadata, logEvent, objectTypeId) => {
  const recipients = getRecipientEmails(logEvent) || [];
  const propertyName = getIn(['recipientProperty'], actionMetadata);
  const objectId = getObjectId(logEvent);
  const contactPropertyLink = inboundDbObjectPropertyUrl(propertyName, objectTypeId, objectId);
  return {
    recipients: recipients.join(', '),
    contactPropertyLink,
    getHelpWithHubspotSupportLink: SupportLinkKeys.GET_HELP_WITH_HUBSPOT
  };
};
const getClassicCopyCompanyPropertyOptions = (actionData, logEvent) => {
  const action = new Action(actionData);
  const flowId = getIn(['flowId'], action);
  const actionId = getIn(['actionId'], action);
  const actionLink = flowEditActionUrl(flowId, actionId);
  const propertyValue = getPropertyValue(logEvent);

  // dev note: this action can technically copy from company >> company as well as company >> contact,
  // but the INVALID_OPTION error can only happen for company >> contact
  const targetPropertyName = getIn(['metadata', 'targetProperty'], action);
  return {
    actionLink,
    propertyValue,
    propertyTypeId: CONTACT_ID,
    propertyName: targetPropertyName,
    updateLifecycleStageSupportLink: SupportLinkKeys.UPDATE_LIFECYCLE_STAGE
  };
};
const getClassicEnrollInWorkflowOptions = () => {
  return {
    suppressContactsSupportLink: SupportLinkKeys.SUPPRESS_CONTACTS
  };
};
const getSlackNotificationOptions = logEvent => {
  return {
    channelsNotFound: /*#__PURE__*/_jsx("b", {
      children: Object.keys(logEvent.contextMap.SLACK_FAILED_CHANNEL_SENDS || {}).map(channel => `#${channel}`).join(', ')
    }),
    usersNotFound: /*#__PURE__*/_jsx("b", {
      children: Object.keys(logEvent.contextMap.SLACK_FAILED_USER_SENDS || {}).map(user => `@${user}`).join(', ')
    }),
    personalizeYourContentLink: SupportLinkKeys.PERSONALIZE_YOUR_CONTENT
  };
};
const getInAppNotificationOptions = actionMetadata => {
  return {
    subject: actionMetadata.subject,
    personalizeYourContentLink: SupportLinkKeys.PERSONALIZE_YOUR_CONTENT
  };
};
const getSendInternalSMSOptions = actionMetadata => {
  return {
    body: actionMetadata.body
  };
};
const getEnrollInFlowOptions = (actionMetadata, logEvent, objectTypeId) => {
  const flowId = getIn(['flowId'], actionMetadata);
  const objectId = getObjectId(logEvent);
  const flowToEnrollInLink = flowDetailsUrl(flowId, BasePaths.ACTION_LOGS, {
    objectId
  });
  return {
    flowId,
    objectType: /*#__PURE__*/_jsx(ObjectTypeName, {
      objectTypeId: objectTypeId
    }),
    flowToEnrollInLink
  };
};
const getWebhookOptions = (actionMetadata, logEvent, objectTypeId) => {
  const webhookResponseBody = getWebhookResponseBody(logEvent);
  const webhookStatusCode = getWebhookStatusCode(logEvent);

  // for errorType REQUIRED_PROPERTY_NOT_FOUND
  const missingProperties = getMissingProperties(logEvent) || [];
  const webhookUrl = getIn(['url'], actionMetadata);
  const webhookMethod = getIn(['method'], actionMetadata) || WebhookRequestTypes.POST;
  const recycleBinLink = recycleBinUrl(objectTypeId);
  return {
    webhookResponseBody,
    webhookStatusCode,
    missingProperties: missingProperties.join(', '),
    webhookUrl,
    webhookMethod,
    objectType: /*#__PURE__*/_jsx(ObjectTypeName, {
      objectTypeId: objectTypeId
    }),
    recycleBinLink,
    learnAboutWebhooksSupportLink: SupportLinkKeys.LEARN_ABOUT_WEBHOOKS,
    getHelpWithHubspotSupportLink: SupportLinkKeys.GET_HELP_WITH_HUBSPOT
  };
};
const getCustomCodeOptions = () => {
  return {
    customCodeDeveloperSupportLink: SupportLinkKeys.CUSTOM_CODE_DEVELOPER
  };
};
const getSalesforceTaskOptions = logEvent => {
  return {
    taskUrl: getSalesforceUrl(logEvent),
    ownerName: getSalesforceOwnerName(logEvent),
    syncContactToSalesforceSupportLink: SupportLinkKeys.SYNC_CONTACT_TO_SALESFORCE
  };
};
const getInvalidPropertiesOption = logEvent => {
  const objectTypeId = getIn(['action', 'metadata', 'objectTypeId'], logEvent);
  return /*#__PURE__*/_jsx(_Fragment, {
    children: (getInvalidProperties(logEvent) || []).map(propertyName => /*#__PURE__*/_jsx(PropertyName, {
      objectTypeId: objectTypeId,
      propertyName: propertyName
    }, propertyName))
  });
};
const getCreateObjectOptions = (logEvent, objectTypeId) => {
  const createdObjectTypeId = getIn(['action', 'metadata', 'objectTypeId'], logEvent);
  const createdObjectId = getCreatedObjectId(logEvent);
  const createdObjectLink = createdObjectTypeId && createdObjectId ? inboundDbObjectUrlByObjectTypeId(createdObjectTypeId, createdObjectId) : null;
  const enrolledObjectId = getObjectId(logEvent);
  const enrolledObjectLink = enrolledObjectId && objectTypeId ? inboundDbObjectUrlByObjectTypeId(objectTypeId, enrolledObjectId) : null;
  const ownerId = getAssignedOwnerId(logEvent);
  const errorType = getErrorType(logEvent);
  if (MissingPropertyErrorTypes.includes(errorType)) {
    return {
      properties: getPropertiesFromMessage(logEvent)
    };
  }
  if (getExecutionState(logEvent) === ExecutionStates.PARTIAL_SUCCESS) {
    return {
      invalidProperties: getInvalidPropertiesOption(logEvent),
      ownerName: getOwnerLabel(logEvent, objectTypeId),
      objectType: /*#__PURE__*/_jsx(ObjectTypeName, {
        objectTypeId: createdObjectTypeId
      })
    };
  }
  return {
    objectType: /*#__PURE__*/_jsx(ObjectTypeName, {
      objectTypeId: createdObjectTypeId
    }),
    objectLink: createdObjectLink || '',
    enrolledObjectType: /*#__PURE__*/_jsx(ObjectTypeName, {
      objectTypeId: objectTypeId
    }),
    enrolledObjectLink,
    ownerName: ownerId ? /*#__PURE__*/_jsx(OwnerName, {
      id: ownerId
    }) : /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-log-events.messageOptionUtils.create-object.noAssignment"
    }),
    associateRecordsSupportLink: SupportLinkKeys.ASSOCIATE_RECORDS
  };
};
const getRotateObjectOptions = (actionMetadata, logEvent, objectTypeId) => {
  const ownerId = getAssignedOwnerId(logEvent);
  const {
    targetProperty
  } = actionMetadata;
  return {
    targetProperty: /*#__PURE__*/_jsx(PropertyName, {
      objectTypeId: objectTypeId,
      propertyName: targetProperty
    }),
    ownerName: /*#__PURE__*/_jsx(OwnerName, {
      id: ownerId || ''
    }),
    objectType: /*#__PURE__*/_jsx(ObjectTypeName, {
      objectTypeId: objectTypeId
    }),
    syncOwnershipSupportLink: SupportLinkKeys.SYNC_OWNERSHIP
  };
};
const getWaitUntilOptions = logEvent => {
  const deferredTimestamp = getDeferredTimestamp(logEvent);
  return {
    expiration: I18n.moment.portalTz(deferredTimestamp).format('lll z')
  };
};
const getEnrollmentOptions = (logEvent, objectTypeId) => {
  const enrollType = getEnrollType(logEvent);
  const triggerEnrollType = getTriggerEnrollType(logEvent);
  switch (enrollType || triggerEnrollType) {
    case EnrollTypes.MANUAL_ENROLL:
      {
        const appId = getEnrollmentAppId(logEvent);
        if (appId) {
          return {
            integrationName: /*#__PURE__*/_jsx(IntegrationName, {
              id: appId
            })
          };
        }
        const userId = getUserId(logEvent);
        return {
          objectTypeName: /*#__PURE__*/_jsx(ObjectTypeName, {
            objectTypeId: objectTypeId
          }),
          // in case the object has been recently deleted
          userName: /*#__PURE__*/_jsx(UserName, {
            id: userId || ''
          })
        };
      }
    case EnrollTypes.ACTION_EXECUTION_ENROLL:
      {
        const classicWorkflowId = getWorkflowId(logEvent);
        const flowId = getSourceFlowId(logEvent);
        if (classicWorkflowId) {
          return {
            workflowName: /*#__PURE__*/_jsx(WorkflowName, {
              flowSourceType: CLASSIC,
              id: classicWorkflowId
            })
          };
        } else if (flowId) {
          return {
            workflowName: /*#__PURE__*/_jsx(WorkflowName, {
              flowSourceType: PLATFORM,
              id: flowId
            })
          };
        }
        break;
      }
    case EnrollTypes.EXTERNAL_TRIGGER_ENROLL:
      {
        const chatflowId = getChatflowId(logEvent);
        if (chatflowId) {
          return {
            chatflowName: /*#__PURE__*/_jsx(ChatflowName, {
              id: chatflowId
            })
          };
        }
        break;
      }
    case EnrollTypes.DYNAMIC_ENROLL:
    case EnrollTypes.RE_ENROLL:
      {
        const trigger = getClassicTrigger(logEvent);
        if (trigger) {
          const triggerId = getClassicTriggerId(logEvent);
          switch (trigger) {
            case TriggerObjectTypes.FORM:
              return {
                link: formUrl(triggerId)
              };
            case TriggerObjectTypes.DYNAMIC_LIST: // same as STATIC_LIST_BULK_ENROLLMENT
            case TriggerObjectTypes.STATIC_LIST_BULK_ENROLLMENT:
              return {
                link: listUrl(triggerId)
              };
            case TriggerObjectTypes.PAGE_VIEW:
              return {
                link: triggerId
              };
            case TriggerObjectTypes.EVENT:
              return {
                link: triggerId
              };
            case TriggerObjectTypes.CONTACT_PROPERTY_NAME:
              return {
                propertyTypeId: logEvent.objectTypeId,
                propertyName: triggerId
              };
            case TriggerObjectTypes.WORKFLOW_ID:
              return {
                link: classicWorkflowUrl(triggerId)
              };
            case TriggerObjectTypes.CHATFLOW_ID:
              {
                const listId = getListId(logEvent);
                if (listId) {
                  return {
                    chatflowName: /*#__PURE__*/_jsx(ChatflowName, {
                      id: listId
                    })
                  };
                }
                break;
              }
            // Explicitly unhandled types (classic's EnrollmentEvent does not have custom messaging):
            // case TriggerObjectTypes.PAGE
            // case TriggerObjectTypes.FOLLOWUP_EMAIL_ID
            // case TriggerObjectTypes.FOLLOWUP_EMAIL_CAMPAIGN_ID
            // case TriggerObjectTypes.EMAIL_OPT
            // case TriggerObjectTypes.CONTACT_PROPERTY_VALUE
            // case TriggerObjectTypes.INTEGRATIONS_TIMELINE_EVENT
            //
            // Explicitly handled types with no link (classic's EnrollmentEvent does not include a link):
            // case TriggerObjectTypes.SINGLE_EMAIL:
            // case TriggerObjectTypes.SINGLE_VID:
            // case TriggerObjectTypes.ENROLL_EXISTING:
            // case TriggerObjectTypes.ACTIVATION_BULK_ENROLLMENT:
            // case TriggerObjectTypes.ENROLLMENT_CRITERIA_MODIFIED_BULK_ENROLLMENT:
            // case TriggerObjectTypes.SEGMENT:
            default:
              return {};
          }
        }
        const filterEventType = getFilterEventType(logEvent);
        if (filterEventType) {
          const flowId = getFlowId(logEvent);
          switch (filterEventType) {
            case FilterEventTypes.MERGE_FILTER:
              return {
                suppressionLink: `${flowUrl(flowId)}/${BasePaths.SETTINGS}/${BasePaths.PLATFORM_FLOW_SUPPRESSIONS}`
              };
            case FilterEventTypes.OBJECT_DELETED_FILTER:
            case FilterEventTypes.OBJECT_DOES_NOT_EXIST_FILTER:
              return {
                objectTypeName: /*#__PURE__*/_jsx(ObjectTypeName, {
                  objectTypeId: objectTypeId
                })
              };
            default:
              return {};
          }
        }
        break;
      }
    case EnrollTypes.BULK_ENROLL:
      {
        const trigger = getClassicTrigger(logEvent);
        if (trigger) {
          const userId = getUserId(logEvent);
          const listId = getListId(logEvent);
          return {
            userName: /*#__PURE__*/_jsx(UserName, {
              id: userId || ''
            }),
            listName: /*#__PURE__*/_jsx(ContactListName, {
              id: listId || ''
            }),
            enrollRecordsAtActivationSupportLink: SupportLinkKeys.ENROLL_RECORDS_THAT_ALREADY_MEET_CRITERIA,
            enrollmentCriteriaModifiedSupportLink: SupportLinkKeys.ENROLL_ACTIVE_LIST_MODIFIED
          };
        }
        break;
      }
    default:
      break;
  }
  return {};
};
const getUnenrollmentOptions = logEvent => {
  const unenrollType = getUnenrollType(logEvent);
  const flowId = getFlowId(logEvent);
  if (unenrollType === UnenrollTypes.MANUAL_UNENROLL) {
    return {
      userName: /*#__PURE__*/_jsx(UserName, {
        id: getUserId(logEvent) || ''
      })
    };
  }
  if (unenrollType === UnenrollTypes.SUPPRESSION_REMOVE_UNENROLL) {
    return {
      link: `${flowUrl(flowId)}/${BasePaths.EDIT}/${BasePaths.PLATFORM_FLOW_CONFIGS}/${BasePaths.PLATFORM_ENROLLMENT}/${BasePaths.FILTERS}`
    };
  }
  if (unenrollType === UnenrollTypes.REMOVE_FROM_WORKFLOWS_SETTING_UNENROLL) {
    const sourceFlowId = getSourceFlowId(logEvent);
    return {
      otherWorkflow: /*#__PURE__*/_jsx(WorkflowName, {
        flowSourceType: PLATFORM,
        id: sourceFlowId
      })
    };
  }
  const classicLogEvent = getClassicLogEvent(logEvent);
  if (classicLogEvent === ClassicUnenrollTypes.UNENROLLED_BY_WORKFLOW) {
    const workflowId = getWorkflowId(logEvent);
    return {
      otherWorkflow: /*#__PURE__*/_jsx(WorkflowName, {
        flowSourceType: CLASSIC,
        id: workflowId
      })
    };
  }
  if (classicLogEvent === ClassicUnenrollTypes.FAILED_ENROLLMENT_ANCHOR_PROPERTY_MISSING_ON_CONTACT || classicLogEvent === ClassicUnenrollTypes.FAILED_ENROLLMENT_ANCHOR_PROPERTY_MISSING_ON_WORKFLOW) {
    return {
      dateCenteredWorkflowsSupportLink: SupportLinkKeys.DATE_CENTERED_WORKFLOWS
    };
  }
  return {
    editorLink: `${flowUrl(flowId)}/${BasePaths.EDIT}`,
    reenrollmentLink: `${flowUrl(flowId)}/${BasePaths.EDIT}/${BasePaths.PLATFORM_FLOW_CONFIGS}/${BasePaths.PLATFORM_ENROLLMENT}/reenrollment`,
    goalLink: `${flowUrl(flowId)}/${BasePaths.GOALS}`,
    suppressionLink: `${flowUrl(flowId)}/${BasePaths.SETTINGS}/${BasePaths.PLATFORM_FLOW_SUPPRESSIONS}`,
    addReenrollmentTriggersSupportLink: SupportLinkKeys.ADD_REENROLLMENT_TRIGGERS
  };
};
const getEnrollmentFailedOptions = (logEvent, objectTypeId) => {
  const flowId = getFlowId(logEvent);
  const revisionHistoryLink = flowChangesUrl(flowId);
  return {
    objectType: /*#__PURE__*/_jsx(ObjectTypeName, {
      objectTypeId: objectTypeId
    }),
    revisionHistoryLink
  };
};
export const getLogEventMessageOptions = (logEvent, objectTypeId) => {
  const {
    type
  } = logEvent;
  const action = getAction(logEvent);
  if (type === LogEventTypes.PRE_ENROLLMENT) {
    // primary messaging comes from ENROLLED, so just grab those options
    return getEnrollmentOptions(logEvent, objectTypeId);
  }
  if (type === LogEventTypes.ENROLLMENT || type === LogEventTypes.UNENROLLMENT) {
    const enrollmentState = getEnrollmentState(logEvent);
    switch (enrollmentState) {
      case EnrollmentStates.ENROLLED:
        return getEnrollmentOptions(logEvent, objectTypeId);
      case EnrollmentStates.UNENROLLED:
        return getUnenrollmentOptions(logEvent);
      case EnrollmentStates.FAILED:
        return getEnrollmentFailedOptions(logEvent, objectTypeId);
      default:
        return {};
    }
  }
  if (type !== LogEventTypes.ACTION_EXECUTION || !action) {
    // Dev note: this previously returned {}, revisionHistoryLink was added for FAIL_IRREPARABLE >> UNKNOWN >> ACTION_DOES_NOT_EXIST
    const flowId = getFlowId(logEvent);
    const revisionHistoryLink = flowChangesUrl(flowId);
    return {
      revisionHistoryLink
    };
  }
  const executionState = getExecutionState(logEvent);
  if (executionState === ExecutionStates.FAIL_RESCHEDULE || executionState === ExecutionStates.DEFERRED) {
    const deferredTimestamp = getDeferredTimestamp(logEvent);
    return {
      datetime: I18n.moment.portalTz(deferredTimestamp).format('lll z')
    };
  }
  if (executionState === ExecutionStates.SKIPPED) {
    return {
      objectInInactiveWorkflowSupportLink: SupportLinkKeys.OBJECT_IN_INACTIVE_WORKFLOW,
      centerOnDateWorkflowSupportLink: SupportLinkKeys.CENTER_ON_DATE_WORKFLOW
    };
  }
  if (executionState === ExecutionStates.PARTIAL_SUCCESS && action.actionType !== ActionTypes.CREATE_OBJECT &&
  // special cased within getCreateObjectOptions
  action.actionType !== ActionTypes.EMAIL_NOTIFICATION // special cased within getEmailNotificationOptions
  ) {
    return getExecutionCounts(logEvent);
  }
  const actionMetadata = getIn(['metadata'], action);
  switch (action.actionType) {
    case ActionTypes.SLACK_NOTIFICATION:
      return getSlackNotificationOptions(logEvent);
    case ActionTypes.EMAIL:
      return getEmailOptions(actionMetadata, logEvent, objectTypeId);
    case ActionTypes.DELAY:
      return getDelayOptions();
    case ActionTypes.TASK:
      return getTaskOptions(logEvent, objectTypeId);
    case ActionTypes.SET_PROPERTY:
      return getSetPropertyOptions(action, logEvent, objectTypeId);
    case ActionTypes.UPDATE_EMAIL_SUBSCRIPTION:
      return getUpdateEmailSubscriptionOptions();
    case ActionTypes.EMAIL_NOTIFICATION:
      return getEmailNotificationOptions(logEvent, actionMetadata);
    case ActionTypes.CLASSIC_NOTIFICATION_EMAIL:
      return getClassicEmailNotificationOptions(actionMetadata, logEvent, objectTypeId);
    case ActionTypes.CLASSIC_COPY_COMPANY_PROPERTY:
      return getClassicCopyCompanyPropertyOptions(action, logEvent);
    case ActionTypes.CLASSIC_ENROLL_IN_WORKFLOW:
      return getClassicEnrollInWorkflowOptions();
    case ActionTypes.IN_APP_NOTIFICATION:
      return getInAppNotificationOptions(actionMetadata);
    case ActionTypes.ENROLL_IN_FLOW:
      return getEnrollInFlowOptions(actionMetadata, logEvent, objectTypeId);
    case ActionTypes.CREATE_OBJECT:
      return getCreateObjectOptions(logEvent, objectTypeId);
    case ActionTypes.ROTATE_OBJECT:
      return getRotateObjectOptions(actionMetadata, logEvent, objectTypeId);
    case ActionTypes.SEND_SMS_NOTIFICATION:
      return getSendInternalSMSOptions(actionMetadata);
    case ActionTypes.AUTOMATION_EXTENSION:
    case ActionTypes.WEBHOOK:
      return getWebhookOptions(actionMetadata, logEvent, objectTypeId);
    case ActionTypes.CUSTOM_CODE:
      return getCustomCodeOptions();
    case ActionTypes.CREATE_SALESFORCE_TASK:
      return getSalesforceTaskOptions(logEvent);
    case ActionTypes.LIST_BRANCH:
      return getListBranchOptions(logEvent, objectTypeId);
    case ActionTypes.STATIC_BRANCH:
      return getStaticBranchOptions(logEvent);
    case ActionTypes.WAIT_UNTIL:
      return getWaitUntilOptions(logEvent);
    default:
      return {};
  }
};